import { isFilterActive, type TargetedTrainee, type TraineeTargetSpec } from "./types";

const intersects = (values: string[], against: Set<string>): boolean =>
  values.some((value) => against.has(value));

/**
 * Pure resolver: turns a {@link TraineeTargetSpec} into the concrete set of trainees.
 *
 * - `statuses` and `excludeTags` are always-AND constraints (scope / hard exclude).
 * - `productIds` and `includeTags` are positive filters combined by `spec.combinator`.
 * - When no filter is active the filter result is empty (audience comes from `manualIds` only),
 *   so an empty spec never accidentally selects everyone.
 * - Finally `manualIds` are unioned on and `excludedIds` removed.
 */
export const resolveTargetedTrainees = (
  trainees: TargetedTrainee[],
  productMembership: Record<string, string[]>,
  spec: TraineeTargetSpec,
): TargetedTrainee[] => {
  const productIds = new Set(spec.productIds);
  const includeTags = new Set(spec.includeTags);
  const excludeTags = new Set(spec.excludeTags);
  const statuses = new Set(spec.statuses);
  const excludedIds = new Set(spec.excludedIds);
  const manualIds = new Set(spec.manualIds);
  const filterActive = isFilterActive(spec);

  const matchedIds = new Set<string>();

  if (filterActive) {
    for (const trainee of trainees) {
      // Scope: status (AND) + hard tag exclude (AND).
      if (statuses.size > 0 && !statuses.has(trainee.status)) {
        continue;
      }
      if (excludeTags.size > 0 && intersects(trainee.tags, excludeTags)) {
        continue;
      }

      // Positive filters combined by the combinator.
      const positives: boolean[] = [];
      if (productIds.size > 0) {
        positives.push(intersects(productMembership[trainee.id] ?? [], productIds));
      }
      if (includeTags.size > 0) {
        positives.push(intersects(trainee.tags, includeTags));
      }

      const passesPositives =
        positives.length === 0
          ? true // only status / exclude active → keep everyone that passed the scope
          : spec.combinator === "AND"
            ? positives.every(Boolean)
            : positives.some(Boolean);

      if (passesPositives) {
        matchedIds.add(trainee.id);
      }
    }
  }

  manualIds.forEach((id) => matchedIds.add(id));
  excludedIds.forEach((id) => matchedIds.delete(id));

  return trainees.filter((trainee) => matchedIds.has(trainee.id));
};
